import { useCallback, useEffect, useRef, useState } from 'react';

import { MouseEventsEnum } from '../constants/shell';

interface IParams {
  /** Initial panel width in px, and the minimum it can be dragged to. */
  defaultWidth: number;
  /**
   * Resolve the maximum width from the container's current width (px). Recomputed on every
   * drag move so the cap can track viewport/container size (e.g. `(w) => w / 2` for 50%).
   */
  getMaxWidth: (containerWidth: number) => number;
}

interface IHandleProps {
  onMouseDown: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

interface IResizablePanel {
  /** Committed width (updated on drag end); apply to the panel element's width. */
  width: number;
  /** Attach to the flex container that holds the panel + handle (used to measure the origin). */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Attach to the panel element being resized (width is written imperatively during drag). */
  panelRef: React.RefObject<HTMLDivElement>;
  /** True while the handle is hovered or actively dragged — drive the handle's visual cue with this. */
  isHandleActive: boolean;
  /** True only while a drag is in progress — render a pointer-capture overlay while this is true. */
  isDragging: boolean;
  /** Spread onto the draggable handle element. */
  handleProps: IHandleProps;
}

/**
 * Drag-to-resize behavior for a horizontal split panel.
 *
 * During a drag the width is written directly to the panel's DOM node (no re-render per mouse
 * move); React state is committed once on release. A full-viewport overlay should be rendered
 * while `isDragging` so the drag keeps tracking over iframes/embeds that would otherwise swallow
 * mouse events. Width is clamped between `defaultWidth` (min) and `getMaxWidth(container width)`.
 */
export default function useResizablePanel({ defaultWidth, getMaxWidth }: IParams): IResizablePanel {
  const [width, setWidth] = useState(defaultWidth);
  const [isHandleActive, setIsHandleActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isResizingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragWidthRef = useRef(defaultWidth);

  const clampWidth = useCallback(
    (rawWidth: number, containerWidth: number) => {
      // Never let the resolved max fall below the min, or the clamp would invert and
      // produce a width narrower than `defaultWidth` on small containers.
      const maxWidth = Math.max(defaultWidth, getMaxWidth(containerWidth));
      return Math.min(maxWidth, Math.max(defaultWidth, rawWidth));
    },
    [defaultWidth, getMaxWidth],
  );

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current || !containerRef.current || !panelRef.current) {
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const nextWidth = clampWidth(e.clientX - containerRect.left, containerRect.width);
      dragWidthRef.current = nextWidth;
      panelRef.current.style.width = `${nextWidth}px`;
    },
    [clampWidth],
  );

  const handleResizeEnd = useCallback(() => {
    isResizingRef.current = false;
    setIsHandleActive(false);
    setIsDragging(false);
    setWidth(dragWidthRef.current);
    document.removeEventListener(MouseEventsEnum.MouseMove, handleResizeMove);
    document.removeEventListener(MouseEventsEnum.MouseUp, handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(() => {
    // Guard against a second mousedown before release stacking duplicate listeners.
    if (isResizingRef.current) {
      return;
    }
    isResizingRef.current = true;
    setIsHandleActive(true);
    setIsDragging(true);
    document.addEventListener(MouseEventsEnum.MouseMove, handleResizeMove);
    document.addEventListener(MouseEventsEnum.MouseUp, handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd]);

  const handleMouseLeave = useCallback(() => {
    if (!isResizingRef.current) {
      setIsHandleActive(false);
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHandleActive(true);
  }, []);

  useEffect(() => {
    return () => {
      document.removeEventListener(MouseEventsEnum.MouseMove, handleResizeMove);
      document.removeEventListener(MouseEventsEnum.MouseUp, handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  // Re-clamp the committed width when the container resizes so the panel never exceeds
  // its (container-relative) max after a viewport change. Skipped while dragging, since the
  // move handler already clamps live.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      if (isResizingRef.current) {
        return;
      }
      const containerWidth = entries[0]?.contentRect.width;
      if (!containerWidth) {
        return;
      }
      setWidth((current) => {
        const clamped = clampWidth(current, containerWidth);
        if (clamped === current) {
          return current;
        }
        // Keep the imperative drag width and any lingering inline style in sync so the
        // committed width actually takes effect (the move handler writes width directly).
        dragWidthRef.current = clamped;
        if (panelRef.current) {
          panelRef.current.style.width = `${clamped}px`;
        }
        return clamped;
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [clampWidth]);

  return {
    width,
    containerRef,
    panelRef,
    isHandleActive,
    isDragging,
    handleProps: {
      onMouseDown: handleResizeStart,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    },
  };
}
